import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import type { UniversalFilterSection, UniversalGroupByOption } from '../components/UniversalTableSearch';

/**
 * ActiveTableContext
 *
 * Provides shared state for multi-table pages where a single filter bar
 * controls whichever table is currently in view.
 *
 * Each table registers itself with metadata (title, filters, search config).
 * The context tracks which table is active based on scroll position.
 * The shared filter bar adapts its UI and applies filters to the active table.
 */

export interface TableFilterState {
  searchValue: string;
  activeFilters: Record<string, string[]>;
  selectedGroupBy: string;
}

export interface RegisteredTable {
  id: string;
  title: string;
  description?: string;
  searchPlaceholder?: string;
  filterSections: UniversalFilterSection[];
  groupByOptions: UniversalGroupByOption[];
  filterState: TableFilterState;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: string, values: string[]) => void;
  onGroupByChange: (groupBy: string) => void;
}

interface ActiveTableContextValue {
  // Table registration
  registerTable: (id: string, config: Omit<RegisteredTable, 'id' | 'filterState'>) => void;
  unregisterTable: (id: string) => void;

  // Active table management
  activeTableId: string | null;
  setActiveTableId: (id: string) => void;
  getActiveTable: () => RegisteredTable | null;

  // Filter state per table
  getFilterState: (tableId: string) => TableFilterState;
  updateFilterState: (tableId: string, updates: Partial<TableFilterState>) => void;

  // All tables
  tables: Map<string, RegisteredTable>;
}

const ActiveTableContext = createContext<ActiveTableContextValue | null>(null);

export const useActiveTableContext = () => {
  const context = useContext(ActiveTableContext);
  if (!context) {
    throw new Error('useActiveTableContext must be used within ActiveTableProvider');
  }
  return context;
};

// Optional hook that returns null if not within provider (for backwards compatibility)
export const useActiveTableContextOptional = () => {
  return useContext(ActiveTableContext);
};

interface ActiveTableProviderProps {
  children: ReactNode;
}

export const ActiveTableProvider: React.FC<ActiveTableProviderProps> = ({ children }) => {
  const [tables, setTables] = useState<Map<string, RegisteredTable>>(new Map());
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  const [filterStates, setFilterStates] = useState<Map<string, TableFilterState>>(new Map());

  // Register a table with its configuration
  const registerTable = useCallback((id: string, config: Omit<RegisteredTable, 'id' | 'filterState'>) => {
    // Initialize filter state if not exists
    setFilterStates((prevStates) => {
      if (prevStates.has(id)) return prevStates;

      const newStates = new Map(prevStates);
      newStates.set(id, {
        searchValue: '',
        activeFilters: {},
        selectedGroupBy: 'none',
      });
      return newStates;
    });

    setTables((prev) => {
      // Don't re-register if already registered
      if (prev.has(id)) return prev;

      const newTables = new Map(prev);

      // Get filter state using a default if not found
      const defaultFilterState = {
        searchValue: '',
        activeFilters: {},
        selectedGroupBy: 'none',
      };

      newTables.set(id, {
        id,
        ...config,
        filterState: defaultFilterState,
      });

      return newTables;
    });

    // Set as active if it's the first table
    setActiveTableId((prev) => prev || id);
  }, []);

  // Unregister a table
  const unregisterTable = useCallback((id: string) => {
    setTables((prev) => {
      const newTables = new Map(prev);
      newTables.delete(id);

      // If this was the active table, switch to the first remaining table
      if (id === activeTableId && newTables.size > 0) {
        setActiveTableId(Array.from(newTables.keys())[0]);
      } else if (newTables.size === 0) {
        setActiveTableId(null);
      }

      return newTables;
    });

    // Clean up filter state
    setFilterStates((prev) => {
      const newStates = new Map(prev);
      newStates.delete(id);
      return newStates;
    });
  }, [activeTableId]);

  // Get the currently active table
  const getActiveTable = useCallback((): RegisteredTable | null => {
    if (!activeTableId) return null;
    const table = tables.get(activeTableId);
    if (!table) return null;

    // Ensure filter state is up to date
    return {
      ...table,
      filterState: filterStates.get(activeTableId) || table.filterState,
    };
  }, [activeTableId, tables, filterStates]);

  // Get filter state for a specific table
  const getFilterState = useCallback((tableId: string): TableFilterState => {
    return filterStates.get(tableId) || {
      searchValue: '',
      activeFilters: {},
      selectedGroupBy: 'none',
    };
  }, [filterStates]);

  // Update filter state for a specific table
  const updateFilterState = useCallback((tableId: string, updates: Partial<TableFilterState>) => {
    setFilterStates((prev) => {
      const newStates = new Map(prev);
      const currentState = newStates.get(tableId) || {
        searchValue: '',
        activeFilters: {},
        selectedGroupBy: 'none',
      };
      newStates.set(tableId, { ...currentState, ...updates });
      return newStates;
    });

    // Also update the table's filterState for consistency
    setTables((prev) => {
      const newTables = new Map(prev);
      const table = newTables.get(tableId);
      if (table) {
        newTables.set(tableId, {
          ...table,
          filterState: { ...table.filterState, ...updates },
        });
      }
      return newTables;
    });
  }, []);

  const value: ActiveTableContextValue = useMemo(() => ({
    registerTable,
    unregisterTable,
    activeTableId,
    setActiveTableId,
    getActiveTable,
    getFilterState,
    updateFilterState,
    tables,
  }), [registerTable, unregisterTable, activeTableId, getActiveTable, getFilterState, updateFilterState, tables]);

  return (
    <ActiveTableContext.Provider value={value}>
      {children}
    </ActiveTableContext.Provider>
  );
};
